<template>
  <div class="complaint-list-table order-log-table-wrap">
    <div class="order-log-table-shell">
      <table class="table table-bordered table-hover order-log-table w-100 mb-0">
        <colgroup>
          <col v-for="col in fields" :key="'col-' + col.key" :style="col.thStyle" />
        </colgroup>
        <thead>
          <tr>
            <th
              v-for="col in fields"
              :key="col.key"
              :class="col.thClass"
              :style="col.thStyle"
            >
              {{ col.label }}
            </th>
          </tr>
        </thead>

        <tbody v-if="loading">
          <tr>
            <td :colspan="columnCount" class="text-center text-primary py-4">
              <b-spinner class="align-middle"></b-spinner>
              <strong class="ml-2">Đang tải...</strong>
            </td>
          </tr>
        </tbody>

        <tbody v-else-if="!items.length">
          <tr>
            <td :colspan="columnCount" class="text-center text-muted py-4">
              Không có dữ liệu
            </td>
          </tr>
        </tbody>

        <tbody
          v-for="(complaint, idx) in items"
          v-else
          :key="complaint.id || idx"
          class="order-log-group"
        >
          <tr class="order-log-meta-row">
            <td :colspan="columnCount" class="order-log-meta-cell">
              <div
                class="d-flex flex-wrap align-items-center"
                style="font-size: 0.87rem"
              >
                <span class="mr-4 d-flex align-items-center">
                  <i
                    class="fas fa-file-alt icon-color text-warning mr-2"
                    v-b-tooltip.hover.bottom
                    title="Phiếu khiếu nại"
                  ></i>
                  <span class="mr-1">Gian hàng:</span>
                  <span class="font-weight-bold text-dark">{{
                    getShopName(complaint) || '—'
                  }}</span>
                </span>
                <span class="mr-4 d-inline-flex align-items-center flex-wrap">
                  <strong class="mr-1">Phiếu KN:</strong>
                  <router-link
                    :to="complaintTicketRoute(complaint)"
                    class="text-primary text-12 mr-2"
                    @click.native.stop
                  >
                    {{ complaint.id }}
                  </router-link>
                  <b-badge :variant="complaint.isClosed ? 'success' : 'warning'">
                    {{ complaint.isClosed ? 'Đã đóng' : 'Đang xử lý' }}
                  </b-badge>
                </span>
                <span v-if="complaint.complaintReason?.name" class="mr-4">
                  <strong class="mr-1">Lý do:</strong>
                  {{ complaint.complaintReason.name }}
                </span>
                <span class="mr-4">
                  <strong class="mr-1">Ngày KN:</strong>
                  {{ formatDateOnly(complaint.complaintDate) }}
                </span>
                <span v-if="complaint.updatedAt" class="mr-4 text-muted">
                  <strong class="mr-1 text-dark">Cập nhật:</strong>
                  {{ formatComplaintTime(complaint.updatedAt) }}
                </span>
              </div>
            </td>
          </tr>

          <tr
            class="order-log-data-row"
            :class="{
              'complaint-data-row--selected': selectedComplaintId === complaint.id,
            }"
            @click="onDataRowClick($event, complaint)"
          >
            <td class="complaint-refs-cell">
              <ul class="list-unstyled mb-0 complaint-refs-list text-12">
                <li
                  v-if="complaint.orderNumber != null && complaint.orderNumber !== ''"
                  class="complaint-ref-line complaint-ref-line--primary"
                >
                  <span class="complaint-ref-label">Mã đơn sàn</span>
                  <span
                    class="complaint-ref-value complaint-ref-order-no"
                    v-b-tooltip.hover.bottom.noninteractive
                    :title="String(complaint.orderNumber)"
                  >{{ complaint.orderNumber }}</span>
                </li>
                <li
                  v-else-if="
                    (complaint.orderNumber == null || complaint.orderNumber === '') &&
                      (complaint.platformOrderId == null || complaint.platformOrderId === '') &&
                      (complaint.orderId == null || complaint.orderId === '')
                  "
                  class="text-muted"
                >
                  —
                </li>
                <li
                  v-if="
                    (complaint.platformOrderId != null && complaint.platformOrderId !== '') ||
                      (complaint.orderId != null && complaint.orderId !== '')
                  "
                  class="complaint-ref-ids-row"
                >
                  <span
                    v-if="complaint.platformOrderId != null && complaint.platformOrderId !== ''"
                    v-b-tooltip.hover.bottom.noninteractive
                    :title="'ID đơn sàn: ' + String(complaint.platformOrderId)"
                    >Sàn {{ complaint.platformOrderId }}</span
                  >
                  <span
                    v-if="
                      (complaint.platformOrderId != null && complaint.platformOrderId !== '') &&
                        (complaint.orderId != null && complaint.orderId !== '')
                    "
                    class="complaint-ref-ids-sep"
                    >·</span
                  >
                  <span
                    v-if="complaint.orderId != null && complaint.orderId !== ''"
                    v-b-tooltip.hover.bottom.noninteractive
                    :title="'ID đơn NB: ' + String(complaint.orderId)"
                    >NB {{ complaint.orderId }}</span
                  >
                </li>
              </ul>
            </td>

            <td class="complaint-detail-cell">
              <ul class="list-unstyled mb-0 order-info-list text-12">
                <li>
                  <span class="font-weight-bold">ĐVVC:</span>
                  {{ complaint.shippingUnit || '—' }}
                </li>
                <li>
                  <span class="font-weight-bold">Bồi hoàn:</span>
                  {{ formatAmountOrDash(complaint.compensationAmount) }}
                </li>
                <li>
                  <span class="font-weight-bold">Tổn thất:</span>
                  {{ formatAmountOrDash(complaint.lossAmount) }}
                </li>
                <li v-if="complaint.feeAmount != null && complaint.feeAmount !== ''">
                  <span class="font-weight-bold">Tiền phí:</span>
                  {{ formatAmountOrDash(complaint.feeAmount) }}
                </li>
                <li v-if="getCloseResultLabel(complaint.closeResultStatus)">
                  <span class="font-weight-bold">Kết quả đóng:</span>
                  {{ getCloseResultLabel(complaint.closeResultStatus) }}
                </li>
              </ul>
            </td>

            <td class="text-center complaint-status-cell">
              <div class="status-combined-cell">
                <b-badge v-if="complaint.processingStatus?.name" variant="secondary">
                  {{ complaint.processingStatus.name }}
                </b-badge>
                <span v-else class="text-muted small">—</span>
                <span class="order-log-sync-time text-muted small d-block mt-1">{{
                  formatComplaintTime(complaint.updatedAt || complaint.complaintDate)
                }}</span>
              </div>
            </td>

            <td class="text-center complaint-money-cell">
              <div class="text-price payment-summary-cell">
                <template v-if="complaint.orderAmount != null && complaint.orderAmount !== ''">
                  <span class="text-muted d-block text-12 mb-0">Tổng đơn</span>
                  <span class="font-weight-bold text-dark d-block">{{
                    formatAmountOrDash(complaint.orderAmount)
                  }}</span>
                </template>
                <span v-else class="text-muted">—</span>
              </div>
            </td>

            <td class="complaint-notes-td text-12">
              <span
                class="complaint-notes-text text-break"
                v-b-tooltip.hover.bottom.noninteractive
                :title="notesTooltip(complaint)"
                >{{ trimNotes(displayNotesForRow(complaint)) }}</span
              >
            </td>

            <td class="text-center">
              <span @click.stop>
                <b-dropdown size="sm" no-caret right class="order-log-actions-dd">
                  <template #button-content>
                    <i class="order-log-actions-dd__icon flaticon2-settings"></i>
                  </template>
                  <b-dropdown-item v-if="!complaint.isClosed" @click="$emit('edit', complaint)">
                    <i class="flaticon2-pen mr-1"></i> Cập nhật
                  </b-dropdown-item>
                  <b-dropdown-item v-if="!complaint.isClosed" @click="$emit('close', complaint)">
                    <i class="flaticon2-check-mark mr-1"></i> Đóng case
                  </b-dropdown-item>
                </b-dropdown>
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { COMPLAINT_TICKET_TABLE_FIELDS } from '../constants';
import { CLIENT_DATE_TIME } from '@/utils/date';
import { convertPrice } from '@/utils/common';

const CLOSE_RESULT_LABELS = {
  money_recovered: 'Đã thu hồi tiền',
  company_support: 'Công ty hỗ trợ khách',
  accept_loss: 'Chấp nhận mất',
};

export default {
  name: 'ComplaintListTable',
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    selectedComplaintId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      fields: COMPLAINT_TICKET_TABLE_FIELDS,
    };
  },
  computed: {
    columnCount() {
      return this.fields.length;
    },
  },
  methods: {
    getShopName(item) {
      return (
        item.storeName ||
        item.shopName ||
        item.platformOrder?.marketplaceConnection?.shopName ||
        null
      );
    },
    complaintTicketRoute(complaint) {
      const q = { complaintId: complaint.id };
      if (complaint.platformOrderId) q.platformOrderId = complaint.platformOrderId;
      return { name: 'platform-integration-complaints', query: q };
    },
    onDataRowClick(ev, complaint) {
      if (ev.target.closest('a, button, .dropdown, input, label')) return;
      this.$emit('row-click', complaint);
    },
    formatAmountOrDash(value) {
      if (value === null || value === undefined || value === '') return '—';
      return convertPrice(value);
    },
    formatDateOnly(val) {
      if (!val) return '—';
      return moment(val).format('DD/MM/YYYY');
    },
    formatComplaintTime(raw) {
      if (!raw) return '—';
      return moment(raw).format(CLIENT_DATE_TIME);
    },
    getCloseResultLabel(code) {
      return code ? CLOSE_RESULT_LABELS[code] || code : null;
    },
    /** Bỏ phần trùng mã đơn sàn ở đầu ghi chú (đã hiển thị ở cột Tham chiếu). */
    displayNotesForRow(complaint) {
      const raw = complaint.notes;
      if (raw == null || !String(raw).trim()) return '';
      let s = String(raw).trim();
      const on =
        complaint.orderNumber != null && complaint.orderNumber !== ''
          ? String(complaint.orderNumber).trim()
          : '';
      if (on && s.startsWith(on)) {
        s = s.slice(on.length).replace(/^[\s\-–—:]+/, '').trim();
      }
      return s;
    },
    notesTooltip(complaint) {
      const raw = complaint.notes;
      if (raw == null || !String(raw).trim()) return '—';
      return String(raw).trim();
    },
    trimNotes(notes, maxLen = 400) {
      if (!notes || !String(notes).trim()) return '—';
      const s = String(notes).trim();
      if (s.length <= maxLen) return s;
      return `${s.slice(0, maxLen - 1)}…`;
    },
  },
};
</script>

<style lang="scss" scoped>
.order-log-table-wrap {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.order-log-table-shell {
  display: block;
  width: 100%;
  min-width: 0;

  .order-log-table {
    width: 100%;
    max-width: none;
    table-layout: fixed;
    border-collapse: collapse;
  }

  .order-log-data-row > td {
    vertical-align: top;
  }

  .order-log-table th,
  .order-log-table td {
    padding: 0.65rem 0.7rem;
    border-top: 1px solid #ecf0f3;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
  }

  .order-log-data-row.complaint-data-row--selected > td {
    background-color: rgba(54, 153, 255, 0.06);
  }

  /* Chỉ một vạch trái — nếu shadow trên mọi td sẽ thành “viền xanh” giữa các cột */
  .order-log-data-row.complaint-data-row--selected > td:first-child {
    box-shadow: inset 3px 0 0 0 #3699ff;
  }

  .order-log-table thead th {
    font-weight: 600;
    color: #3f4254;
    background-color: #f3f6f9;
  }

  .order-log-meta-cell {
    padding: 0.65rem 1rem !important;
    border-bottom: none !important;
    background: linear-gradient(180deg, #f9fafb 0%, #f3f6f9 100%);
    border-left: 3px solid #e1e3ea;
  }
}

.order-log-table {
  background: white;
  border-radius: 4px;
  width: 100%;

  :deep(th) {
    white-space: normal;
    vertical-align: middle;
  }
}

.complaint-list-table {
  width: 100%;
  min-width: 0;
}

.complaint-refs-list > li + li {
  margin-top: 0.35rem;
}

.complaint-ref-line--primary {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.2rem;
}

.complaint-ref-line--primary .complaint-ref-label {
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #7e8299;
}

.complaint-ref-order-no {
  font-weight: 600;
  font-size: 0.8125rem;
  color: #181c32;
  line-height: 1.4;
  word-break: break-all;
}

.complaint-ref-ids-row {
  font-size: 0.72rem;
  line-height: 1.35;
  color: #7e8299;
}

.complaint-ref-ids-sep {
  margin: 0 0.28rem;
  opacity: 0.7;
}

.complaint-notes-td {
  vertical-align: top !important;
}

.complaint-notes-text {
  display: block;
  color: #3f4254;
  line-height: 1.45;
}

.order-info-list li {
  margin-bottom: 6px;
  line-height: 1.45;
}

.order-info-list li:last-child {
  margin-bottom: 0;
}

.complaint-refs-cell {
  word-break: normal;
  overflow-wrap: anywhere;
  overflow: visible;
  vertical-align: top !important;
}

.complaint-refs-cell .complaint-ref-line:not(.complaint-ref-line--primary) {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.25rem 0.35rem;
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.complaint-ref-line:not(.complaint-ref-line--primary) .complaint-ref-label {
  flex-shrink: 0;
  white-space: nowrap;
  font-weight: 700;
}

.complaint-ref-line:not(.complaint-ref-line--primary) .complaint-ref-value {
  flex: 1 1 auto;
  min-width: 0;
  white-space: normal;
  word-break: break-all;
  font-weight: 500;
}

.order-log-data-row > td.complaint-notes-td {
  min-width: 0;
}

.text-price {
  font-size: 12px;
}

.status-combined-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  padding: 0.1rem 0;
}

.status-combined-cell :deep(.badge) {
  font-weight: 600;
  font-size: 0.7rem;
  letter-spacing: 0.03em;
  padding: 0.35em 0.65em;
}

.order-log-sync-time {
  font-size: 0.72rem;
  opacity: 0.9;
  max-width: 100%;
  text-align: center;
}

.order-log-actions-dd :deep(> .btn) {
  min-width: 2.1rem;
  min-height: 2.1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  background: #fff;
  color: #7e8299;
}

.order-log-actions-dd__icon {
  font-size: 1rem;
  line-height: 1;
  padding: 0;
}
</style>
